/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-nested-ternary */
import React, { ReactNode, useEffect, useState } from "react";
import { Box, Button, Divider, Grid, MenuItem, TextField, Typography } from "@mui/material";

import Panel from "../../../../../components/Panel";
import { rupeeDisplay } from "../../../../../helpers/rupee";
import { t, tl } from "../../../../../components/translate";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { Employee, UserMode } from "../../../../../interfaces/User";
import useRcUsesCitPfSsf from "../../../../../hooks/useRcUsesCitPfSsf";
import { getPfSsfLabel } from "../../../../Payroll/Salary/SalaryCreateEdit";
import { useDebouncedCallback } from "../../../../../hooks/useDebouncedCallback";
import useCurrentResourceCentre from "../../../../../hooks/useCurrentResourceCentre";
import { ServiceProvider } from "../../../../../interfaces/ServiceProvidersInterface";
import EmployeeAndSpSelect from "../../../../../components/Employee/EmployeeAndSpSelect";
import CalendarDropdown from "../../../../../components/CalendarDropdown/CalendarDropdown";
import {
  usePostCtc,
  usePatchCtc,
  useCalculateCtcSummary
} from "../../../../../hooks/query/useCtcs";

import {
  ICtcFormData,
  ICtc,
  TSalaryPeriodicity,
  ICtcSummary
} from "../../../../../interfaces/CtcInterface";

import {
  MonthlyAllowanceComponents,
  YearlyEarningsComponents
} from "../../../../../config/payroll";

import {
  RetirementFundType,
  SalaryPeriodicity,
  TRetirementFundType
} from "../../../../../constants/payroll";

function CtcSummaryRow({
  isLoading,
  label = "",
  value
}: {
  isLoading: boolean;
  label: string;
  value: any;
}) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" align="right">
        {isLoading ? "..." : rupeeDisplay(value || 0)}
      </Typography>
    </Box>
  );
}

// eslint-disable-next-line import/prefer-default-export
export function CtcCreateEdit({
  onClose,
  ctc,
  onSuccess
}: {
  onClose: () => void;
  ctc?: Partial<ICtc> | null;
  onSuccess: (data?: ICtc) => void;
}): ReactNode {
  const isEdit = !!ctc?.id;

  const { mutate: postCtcMutation } = usePostCtc();
  const { mutate: patchCtcMutation } = usePatchCtc();
  const { mutate: calculateCtcSummary, isLoading } = useCalculateCtcSummary();

  const rc = useCurrentResourceCentre();
  const { rcUsesProvidentFund, rcUsesSocialSecurityFund } = useRcUsesCitPfSsf();

  // local states
  const [confirmEdit, setConfirmEdit] = useState<boolean>(false);
  const [confirmCreate, setConfirmCreate] = useState<boolean>(false);

  // form states
  const [remarks, setRemarks] = useState<string>(ctc?.remarks || "");
  const [basicSalary, setBasicSalary] = useState<number>(ctc?.monthlyEarnings?.basicSalary || 0);

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | ServiceProvider | null>(
    isEdit ? ctc?.employee || ctc?.serviceProvider || null : null
  );

  const [effectiveFrom, setEffectiveFrom] = useState<string>(
    ctc?.effectiveFrom ?? new Date().toISOString()
  );

  const [allowances, setAllowances] = useState<
    Record<string, { value: number; periodicity: TSalaryPeriodicity }>
  >(ctc?.monthlyEarnings?.allowances || {});

  const [yearlyEarnings, setYearlyEarnings] = useState<
    Record<string, { value: number; periodicity: TSalaryPeriodicity }>
  >(ctc?.yearlyEarnings || {});

  const [retirementFundType, setRetirementFundType] = useState<
    Exclude<TRetirementFundType, typeof RetirementFundType.CITIZEN_INVESTMENT_TRUST>
  >(
    ctc?.retirementFundType ||
      (rcUsesProvidentFund
        ? RetirementFundType.PROVIDENT_FUND
        : rcUsesSocialSecurityFund
        ? RetirementFundType.SOCIAL_SECURITY_FUND
        : RetirementFundType.NONE)
  );

  const [ctcSummary, setCtcSummary] = useState<ICtcSummary>({
    basicSalary: 0,
    totalMonthlyAllowances: 0,
    totalYearlyEarnings: 0,
    pfContributions: { employee: 0, employer: 0 },
    ssfContributions: { employee: 0, employer: 0 },
    employeeMonthlyContribution: 0,
    employerMonthlyContribution: 0,
    baseMonthlyCompensation: 0,
    monthlyCtc: 0,
    annualBaseCtc: 0,
    annualCtc: 0
  });

  // get pf, ssf, cit label
  const pfSsfCitLabel = getPfSsfLabel({
    pf: retirementFundType === RetirementFundType.PROVIDENT_FUND,
    ssf: retirementFundType === RetirementFundType.SOCIAL_SECURITY_FUND
  });

  const buildFormData = (): ICtcFormData => {
    const isEmployee =
      selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.Employee ||
      Boolean(
        selectedEmployee &&
          "nonUserEmployee" in selectedEmployee &&
          selectedEmployee.nonUserEmployee
      );
    const isServiceProvider =
      selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.ServiceProvider;

    return {
      resourceCentreId: Number(rc?.id),
      employeeId: isEmployee ? selectedEmployee?.id : undefined,
      serviceProviderId: isServiceProvider ? selectedEmployee?.id : undefined,
      effectiveFrom,
      remarks,
      monthlyEarnings: {
        basicSalary,
        allowances
      },
      yearlyEarnings,
      retirementFundType
    };
  };

  // hande create
  const handleCreate = () => {
    const formData = buildFormData();
    postCtcMutation(formData, {
      onSuccess: (response) => {
        if (response.success) {
          onSuccess(response.data);
          onClose();
        }
      }
    });
    setConfirmCreate(false);
  };

  // handle update
  const handleUpdate = () => {
    if (!ctc?.id) return;
    const formData = buildFormData();
    patchCtcMutation(
      { id: ctc.id, data: formData },
      {
        onSuccess: (response) => {
          if (response.success) {
            onSuccess(response.data);
            onClose();
          }
        }
      }
    );
    setConfirmEdit(false);
  };

  const debouncedCalculateCtc = useDebouncedCallback(() => {
    if (basicSalary > 0) {
      calculateCtcSummary(
        {
          monthlyEarnings: {
            basicSalary,
            allowances
          },
          yearlyEarnings,
          retirementFundType
        },
        {
          onSuccess: (response) => {
            if (response.success && response.data) {
              setCtcSummary(response.data);
            }
          }
        }
      );
    }
  }, 1000);

  // use effects
  useEffect(() => {
    debouncedCalculateCtc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicSalary, allowances, yearlyEarnings, retirementFundType]);

  return (
    <Panel
      title={isEdit ? t("ctc.edit") : t("ctc.create")}
      onClose={onClose}
      footer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-end",
            gap: 2
          }}
        >
          <Button onClick={onClose} sx={{ height: "32px", width: "60px" }}>
            {tl("cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={() => {
              if (isEdit) {
                setConfirmEdit(true);
              } else {
                setConfirmCreate(true);
              }
            }}
            sx={{ height: "35px", width: "80px" }}
          >
            {isEdit ? tl("update") : tl("create")}
          </Button>
        </Box>
      }
    >
      {confirmCreate && (
        <OkhatiDialog
          title={tl("ctc.create")}
          description="Are you sure you want to create ctc? Please review the details before proceeding."
          next={handleCreate}
          cancel={() => setConfirmCreate(false)}
          readMode={false}
        />
      )}

      {confirmEdit && (
        <OkhatiDialog
          title={tl("update")}
          description="Are you sure you want to update ctc? Please review the details before proceeding."
          next={handleUpdate}
          cancel={() => setConfirmEdit(false)}
          readMode={false}
        />
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* ctc summary */}
        <Box
          sx={{
            background: "#f7f7f7",
            borderRadius: 2,
            p: 2,
            border: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
            CTC Summary
          </Typography>

          <CtcSummaryRow
            isLoading={isLoading}
            label="Basic Salary"
            value={ctcSummary?.basicSalary}
          />

          <CtcSummaryRow
            isLoading={isLoading}
            label="Monthly Allowances"
            value={ctcSummary?.totalMonthlyAllowances ?? 0}
          />

          <Grid size={12}>
            <Divider sx={{ my: "4px" }} />
          </Grid>

          <CtcSummaryRow
            isLoading={isLoading}
            label="Monthly CTC (Excl. Employer Contributions)"
            value={ctcSummary?.baseMonthlyCompensation ?? 0}
          />

          <CtcSummaryRow
            isLoading={isLoading}
            label={
              retirementFundType
                ? `Monthly ${pfSsfCitLabel} Contributions (from Employer)`
                : "Monthly Employer Contributions"
            }
            value={ctcSummary?.employerMonthlyContribution ?? 0}
          />

          <Grid size={12}>
            <Divider sx={{ my: "4px" }} />
          </Grid>

          <CtcSummaryRow
            isLoading={isLoading}
            label="Monthly CTC (Incl. Employer Contributions)"
            value={ctcSummary?.monthlyCtc ?? 0}
          />

          <Grid size={12}>
            <Divider sx={{ my: "4px" }} />
          </Grid>

          <CtcSummaryRow
            isLoading={isLoading}
            label="Yearly CTC (Excl. Yearly Earnings)"
            value={ctcSummary?.annualBaseCtc ?? 0}
          />

          <CtcSummaryRow
            isLoading={isLoading}
            label="Yearly Earnings"
            value={ctcSummary?.totalYearlyEarnings ?? 0}
          />

          <CtcSummaryRow
            isLoading={isLoading}
            label="Yearly CTC (Incl. Yearly Earnings)"
            value={ctcSummary?.annualCtc ?? 0}
          />
        </Box>

        {/* basic details */}
        <Box>
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid
              size={{ xs: 12, sm: 6 }}
              sx={{
                display: "flex",
                alignItems: "center"
              }}
            >
              <EmployeeAndSpSelect
                TextFieldProps={{ required: true, "data-testmation": "employeeSelect" } as any}
                disabled={isEdit}
                selectedEmployee={selectedEmployee}
                onChange={(emp) => {
                  setSelectedEmployee(emp);
                  // reset fields
                  setEffectiveFrom(new Date().toISOString());
                  setRemarks("");
                  setBasicSalary(0);
                  setAllowances({});
                  setYearlyEarnings({});
                }}
              />
            </Grid>

            <Grid
              size={{ xs: 12, sm: 6 }}
              sx={{
                display: "flex",
                alignItems: "center"
              }}
            >
              <CalendarDropdown
                fullwidth
                TextFieldProps={{ variant: "outlined" }}
                label="Effective from Date"
                placeholder="Select Effective from Date"
                date={new Date(effectiveFrom)}
                onChange={(val) => setEffectiveFrom(val.toISOString())}
              />
            </Grid>

            <TextField
              select
              label="Fund Type"
              value={retirementFundType}
              onChange={(e) =>
                setRetirementFundType(
                  e.target.value as Exclude<
                    TRetirementFundType,
                    typeof RetirementFundType.CITIZEN_INVESTMENT_TRUST
                  >
                )
              }
              variant="outlined"
              fullWidth
              size="small"
              data-testmation="fundType"
              slotProps={{ inputLabel: { shrink: true } }}
            >
              <MenuItem value={RetirementFundType.NONE} data-testmation="fundType-none">
                None
              </MenuItem>
              <MenuItem value={RetirementFundType.PROVIDENT_FUND} data-testmation="fundType-pf">
                Provident Fund
              </MenuItem>
              <MenuItem
                value={RetirementFundType.SOCIAL_SECURITY_FUND}
                data-testmation="fundType-ssf"
              >
                Social Security Fund
              </MenuItem>
            </TextField>

            <Grid size={{ xs: 12 }}>
              <TextField
                type="number"
                variant="outlined"
                label="Basic Salary (Monthly)"
                fullWidth
                value={basicSalary}
                data-testmation="basicSalary"
                slotProps={{
                  inputLabel: {
                    shrink: true
                  }
                }}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setBasicSalary(Number.isNaN(value) ? 0 : value);
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* monthly allowances */}
        <Box>
          <Typography sx={{ mb: 2 }}>Monthly Allowances</Typography>
          <Grid container spacing={2}>
            {[
              MonthlyAllowanceComponents.HOUSE_RENT_ALLOWANCE,
              MonthlyAllowanceComponents.CONVEYANCE_ALLOWANCE,
              MonthlyAllowanceComponents.DEARNESS_ALLOWANCE,
              MonthlyAllowanceComponents.MEDICAL_ALLOWANCE,
              MonthlyAllowanceComponents.OTHER_ALLOWANCE
            ].map((field) => (
              <Grid key={field} size={{ xs: 12, sm: 6 }}>
                <TextField
                  name={field}
                  type="number"
                  variant="outlined"
                  fullWidth
                  value={allowances[field]?.value || 0}
                  label={field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  data-testmation={`allowance-${field}`}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    setAllowances((prev) => ({
                      ...prev,
                      [field]: {
                        ...prev[field],
                        value: Number.isNaN(value) ? 0 : value,
                        periodicity:
                          prev[field]?.periodicity ??
                          (SalaryPeriodicity.MONTHLY as TSalaryPeriodicity)
                      }
                    }));
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* annual benefits */}
        <Box>
          <Typography sx={{ mb: 2 }}>Annual/Yearly Benefits (One time earnings)</Typography>
          <Grid container spacing={2}>
            {/* TODO: instead of using allowance, use bonuse like festivalBonus */}
            {[YearlyEarningsComponents.FESTIVAL_ALLOWANCE].map((field) => (
              <Grid key={field} size={{ xs: 12, sm: 6 }}>
                <TextField
                  name={field}
                  type="number"
                  variant="outlined"
                  fullWidth
                  value={yearlyEarnings[field]?.value || 0}
                  label={field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  data-testmation={`yearlyEarnings-${field}`}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    setYearlyEarnings((prev) => ({
                      ...prev,
                      [field]: {
                        ...prev[field],
                        value: Number.isNaN(value) ? 0 : value,
                        periodicity:
                          prev[field]?.periodicity ??
                          (SalaryPeriodicity.YEARLY as TSalaryPeriodicity)
                      }
                    }));
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* remarks */}
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Remarks
          </Typography>
          <TextField
            name="remarks"
            variant="outlined"
            value={remarks}
            data-testmation="remarks"
            fullWidth
            multiline
            rows={4}
            placeholder="Enter any remarks or notes"
            onChange={(e) => setRemarks(e.target.value)}
          />
        </Box>
      </Box>
    </Panel>
  );
}
